import * as React from 'react'
import { ReactElement } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Loading from '../../../../components/Loading'
import useAxios from '../../../../hooks/useAxios'
import useEntity from '../../../../hooks/useEntity'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../../hooks/useAttributes'
import { CreateDto } from '../../../../config/filePartyConfig'
import { applyFolderIntent } from '../../../components/Input/folderPath'
import PartyForm, { AddPartyProps, PartyStatus } from '../../../../components/Form/Party/PartyForm'
import { getLegalPersonValidationSchema, LegalPersonMutationDto } from '../../../config/partyMutationConfig'
import LegalPersonFormBody from './LegalPersonFormBody'
import UnsavedChangesGuard from '../../../components/Form/UnsavedChangesGuard'
import { partyGoBack } from './partyGoBack'

/** v9 add screen for a legal person (HAM-3911), from the dossier or the general party overview. */
export default function AddLegalPersonPage(): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const location = useLocation()
    const { t: translate } = useTranslation()
    const { fileId } = useParams<{ fileId?: string }>()

    const attributes = useAttributes(AttributeViewType.Input, [AttributeContextType.LegalPerson], undefined, EntityAttributeType.Party)

    // Populate the payment term fields from the backend scaffold.
    const [scaffold] = useEntity<LegalPersonMutationDto>(() => ({ url: '/party/legal/scaffold', method: 'POST' }), false)

    const initialValues = { ...scaffold.data, status: PartyStatus.Active } as LegalPersonMutationDto
    attributes.applyDefaultValues(initialValues)

    if (!scaffold.loaded) {
        return <Loading />
    }

    const goBack = partyGoBack(history)

    return (
        <PartyForm<LegalPersonMutationDto>
            formikProps={{
                initialValues,
                validationSchema: getLegalPersonValidationSchema(translate, attributes.activeAttributeDefinitions),
            }}
            onSubmit={async (party: LegalPersonMutationDto) => {
                await applyFolderIntent(axios, party)
                return await axios.post<string>('/party/legal', {
                    ...party,
                    pivot: fileId ? ({ fileId, ...((location.state as AddPartyProps) || {}) } as CreateDto) : undefined,
                })
            }}
            goBack={goBack}
            attributes={attributes}
        >
            <LegalPersonFormBody goBack={goBack} attributes={attributes} />
            <UnsavedChangesGuard />
        </PartyForm>
    )
}
